import React, { useEffect } from "react";
import { Container } from "../../../components";
import { Text, Heading } from "@telia/teddy";
import _first from "lodash/first";
import _isEmpty from "lodash/isEmpty";
import _values from "lodash/values";
import moment from "moment";
import { useSelector } from "react-redux";
import { CUSTOMER_TYPE } from "../../../../../constants";
import PAGES from "../../../../../constants/pages";
import { RootState } from "../../../../../models";
import ContactCard from "../../../checkout/components/molecules/ContactCard/ContactCard";
import { isNewPhysicalSimRequired } from "../../../checkout/state/selectors/combinedSelectors";
import { isEsimSelected } from "../../../checkout/state/selectors/customerInfoSelectors";
import { getFormById } from "../../../checkout/state/selectors/formSelectors";
import { getPhoneNumber, getPhoneNumberCheckType } from "../../../checkout/state/selectors/phoneNumberSelectors";
import { getSubscriptions } from "../../../checkout/state/selectors/subscriptionsSelectors";
import { getFormattedPhoneNumber } from "../../../rapidform/formValidation";
import {
    getAddedServices,
    getDeliveryAddress,
    getEmailAddress,
    getEmployeeFullName,
    getRequestedPortinDate,
    isFormerLegalOwnerSameAsBankId,
    isSubmitSuccess,
} from "../../state/selectors";
import "./Summary.less";

const Summary: React.FC = () => {
    const deliveryAddress = useSelector((state: RootState) => getDeliveryAddress(state));
    const editDeliveryAddressForm = useSelector((state: RootState) => getFormById(state, "editDeliveryAddressForm"));
    const name = useSelector((state: RootState) => getEmployeeFullName(state));
    const isPhysicalSimRequired = useSelector((state: RootState) => isNewPhysicalSimRequired(state));
    const subscriptions = useSelector((state: RootState) => getSubscriptions(state));
    const selectedSubscription = _first(_values(subscriptions));
    const phoneNumberData = useSelector((state: RootState) => getPhoneNumber(state, selectedSubscription?.id)) || {};
    const customerType = useSelector((state: RootState) => getPhoneNumberCheckType(state, selectedSubscription?.id));
    const requestPortinDate = useSelector((state: RootState) => getRequestedPortinDate(state)) || {};
    const portinDate = requestPortinDate ? moment(requestPortinDate).format("DD.MM.YYYY [kl.] HH:mm") : undefined;
    const isEsim = useSelector((state: RootState) => isEsimSelected(state));
    const email = useSelector((state: RootState) => getEmailAddress(state));
    const submitSuccess = useSelector((state: RootState) => isSubmitSuccess(state));
    const formerLegalOwnerSameAsBankId = useSelector((state: RootState) => isFormerLegalOwnerSameAsBankId(state));
    const addedServices = useSelector((state: RootState) => {
        const services = getAddedServices(state) || [];
        const simServices = services.reduce((acc: Record<string, string>, service: string) => {
            if (
                service === "TvillingSIM" ||
                service === "DataSIM" ||
                service === "TvillingSIM-eSIM" ||
                service === "DataSIM-eSIM"
            ) {
                const count = Object.keys(acc).filter((key) => key.startsWith(service)).length + 1;
                acc[`${service}${count > 1 ? `-${count}` : ""}`] = `${service}${count > 1 ? `-${count}` : ""}`;
            }
            return acc;
        }, {});
        return Object.values(simServices).join(", ");
    });

    const hasDeliveryAddress = ![deliveryAddress?.street, deliveryAddress?.postcode, deliveryAddress?.city].every(
        (item) => !item,
    );

    useEffect(() => {
        if (submitSuccess) {
            window.location.assign(PAGES.ONBOARDING_B2B.RECEIPT.URL);
        }
    }, [submitSuccess]);

    return (
        <Container
            className="onboarding-b2b__section"
            size="small"
            whiteBg
            noPaddingTop
            noMargin
            data-cy="contact-card"
        >
            <Heading mb="100" as="h1" variant="title-600" className="onboarding-b2b__page-heading">
                {"Oppsummering"}
            </Heading>
            <Container className="onboarding-b2b__summary">
                <Heading mb="100" as="h2" variant="title-200" className="onboarding-b2b__heading">
                    {"Din ordre"}
                </Heading>
                <div className="onboarding-b2b__summary-wrapper">
                    <div className="onboarding-b2b__summary-container">
                        <Text mb="100" data-di-mask>
                            <span className="onboarding-b2b__summary-labels">Valgt nummer:</span>
                            {getFormattedPhoneNumber(phoneNumberData.selectedPhoneNumber)}
                        </Text>
                        {(customerType === CUSTOMER_TYPE.PORT_IN ||
                            (customerType === CUSTOMER_TYPE.EXISTING && !formerLegalOwnerSameAsBankId)) && (
                            <Text mb="100">
                                <span className="onboarding-b2b__summary-labels"> Porteringsdato:</span>
                                {portinDate} (satt av ny arbeidsgiver)
                            </Text>
                        )}
                        {customerType !== CUSTOMER_TYPE.EXISTING && (
                            <Text mb="100">
                                <span className="onboarding-b2b__summary-labels">SIM:</span>
                                {isEsim ? "eSIM" : "Fysisk SIM-kort"}
                            </Text>
                        )}
                        {!_isEmpty(addedServices) && (
                            <Text mb="100">
                                <span className="onboarding-b2b__summary-labels">Tilleggstjenester:</span>
                                {addedServices}
                            </Text>
                        )}
                        <Text mb="100" data-di-mask>
                            <span className="onboarding-b2b__summary-labels">E-post:</span>
                            {email}
                        </Text>
                    </div>
                </div>
            </Container>
            {!_isEmpty(deliveryAddress) && isPhysicalSimRequired && hasDeliveryAddress && (
                <Container className="onboarding-b2b__delivery">
                    <Heading mb="100" as="h2" variant="title-200" className="onboarding-b2b__heading">
                        {"Bruker og mottaker"}
                    </Heading>
                    <div className="onboarding-b2b__deliveryaddress-wrapper">
                        <div className="onboarding-b2b__deliveryaddress-container">
                            <ContactCard
                                headingText=""
                                name={name as string}
                                address={deliveryAddress?.street}
                                city={deliveryAddress.city}
                                deliveryAddressForm={editDeliveryAddressForm}
                                contactPhone={""}
                                contactEmail={""}
                                showContactInfo={false}
                                isSummary={true}
                            />
                            <Text mb="100" className="onboarding-b2b__free-delivery">
                                SIM-kort leveres{" "}
                                <span className="onboarding-b2b__free-delivery--bold">kostnadsfritt</span> i postkassen.
                            </Text>
                        </div>
                    </div>
                </Container>
            )}
        </Container>
    );
};

export default Summary;
